import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBullhorn,
  FaChevronDown,
  FaChevronRight,
  FaDownload,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFacebook,
  FaFolderOpen,
  FaGlobe,
  FaGoogle,
  FaInstagram,
  FaLinkedin,
  FaPen,
  FaPhoneAlt,
  FaShareAlt,
  FaTwitter,
  FaUserCircle,
  FaWhatsapp,
  FaYoutube,
} from "react-icons/fa";
import { ErrorBox } from "./components/common";
import ProjectList from "./components/ProjectList";
import { ResultsMobileCard, ResultsMobileList } from "../../components/ResultsMobile";
import ClientProjectForm from "./components/ClientProjectForm";
import ClientServiceForm from "./components/ClientServiceForm";
import axiosInstance from "../../components/api/axiosInstance";
import { errorMessage, staffDmApi } from "./dmApi";
import { portalTypeLabel } from "../../utils/loginDomain";
import "./dm.css";

const SERVICE_INFO = {
  SEO: { label: "SEO", title: "Search engine optimisation", tone: "organic" },
  SMO: { label: "SMO", title: "Social media optimisation", tone: "organic" },
  SEM: { label: "SEM", title: "Search engine marketing (Google Ads)", tone: "paid" },
  SMM: { label: "SMM", title: "Social media marketing (Meta / LinkedIn ads)", tone: "paid" },
  CNT: { label: "Content", title: "Content creation", tone: "content" },
};

const PLATFORM_ICONS = [
  [/facebook|meta/i, FaFacebook, "#1877f2"],
  [/instagram/i, FaInstagram, "#e1306c"],
  [/linkedin/i, FaLinkedin, "#0a66c2"],
  [/twitter|\bx\b/i, FaTwitter, "#0f172a"],
  [/youtube/i, FaYoutube, "#ff0000"],
  [/whatsapp/i, FaWhatsapp, "#25d366"],
  [/google|gmb|gmail/i, FaGoogle, "#ea4335"],
  [/mail/i, FaEnvelope, "#475569"],
];

function platformIcon(name) {
  const match = PLATFORM_ICONS.find(([re]) => re.test(name || ""));
  return match ? { Icon: match[1], color: match[2] } : { Icon: FaShareAlt, color: "#64748b" };
}

const initials = (text) =>
  String(text || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const websiteHref = (url) => (/^https?:/i.test(url) ? url : `https://${url}`);

const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "inactive" || s === "suspended") return "danger";
  return "neutral";
};

function Info({ label, value, href, external }) {
  const empty = value == null || String(value).trim() === "";
  return (
    <div className="dmc-info">
      <span className="dmc-info__label">{label}</span>
      {empty ? (
        <span className="dmc-info__value is-empty">—</span>
      ) : href ? (
        <a className="dmc-info__value is-link" href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>
          {value}
        </a>
      ) : (
        <span className="dmc-info__value">{value}</span>
      )}
    </div>
  );
}

/** Staff: one DM client's profile, own projects, Digital Marketing projects and social accounts. */
function DMClientDetails({ api = staffDmApi, basePath = "/dm-projects" }) {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [dmProjectCount, setDmProjectCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addingProject, setAddingProject] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");

  const reloadOverview = () =>
    api
      .overview(tenantId)
      .then(setOverview)
      .catch((err) => setError(errorMessage(err, "Could not reload client")));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([api.overview(tenantId), api.meta()])
      .then(([ov, m]) => {
        if (cancelled) return;
        setOverview(ov);
        setMeta(m);
      })
      .catch((err) => !cancelled && setError(errorMessage(err, "Could not load client")))
      .finally(() => !cancelled && setLoading(false));
    api
      .listProjects({ tenant_id: tenantId })
      .then((rows) => !cancelled && setDmProjectCount((rows || []).length))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, tenantId]);

  if (loading) {
    return (
      <div className="dmp">
        <div className="dmp-card dmp-empty">Loading client…</div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="dmp">
        <Link to={basePath} className="dmp-muted">
          ← Change client
        </Link>
        <ErrorBox message={error || "Client not found"} />
      </div>
    );
  }

  const access = overview.Dm_Access;
  const enrolled = access?.allowed_by_business_type && access?.enabled;
  const services = access?.services || [];
  const name = overview.Business_Name || overview.Client_Name || `Client #${overview.tenant_id}`;
  const tenantProjects = overview.Tenant_Projects || [];
  const tenantServices = overview.Tenant_Services || [];
  const isServiceBased = overview.Business_Model === "Service";
  const modelLabel = overview.Business_Model ? `${overview.Business_Model} based` : null;
  const offerings = isServiceBased ? tenantServices : tenantProjects;
  const serviceTerm = projectSearch.trim().toLowerCase();
  const filteredServices = serviceTerm
    ? tenantServices.filter((s) =>
        [s.Service_Name, s.Description].some((v) => String(v || "").toLowerCase().includes(serviceTerm))
      )
    : tenantServices;
  const projectTerm = projectSearch.trim().toLowerCase();
  const filteredProjects = projectTerm
    ? tenantProjects.filter((p) =>
        [p.Project_Name, p.Short_Code, p.Project_Type, p.Address].some((v) =>
          String(v || "").toLowerCase().includes(projectTerm)
        )
      )
    : tenantProjects;
  const downloadDetails = async (p) => {
    try {
      const res = await axiosInstance.get(`/tenant-projects/${p.Tenant_Project_Id}/property-details`);
      const blob = new Blob([JSON.stringify(res.data?.data ?? {}, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(p.Short_Code || p.Project_Name || "project").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-")}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(errorMessage(err, "Could not download the property details"));
    }
  };
  const accounts = overview.Social_Accounts || [];
  const activeAccounts = accounts.filter((a) => String(a.Status || "").toLowerCase() === "active").length;
  const editTenant = () => navigate(`/tenants/${overview.tenant_id}/edit`);
  const manageAccounts = () => navigate(`/tenants/${overview.tenant_id}/view`);

  const tabs = [
    { key: "profile", label: "Client Details" },
    { key: "accounts", label: "Social Accounts", count: accounts.length || null },
    { key: "projects", label: isServiceBased ? "Services" : "Projects", count: offerings.length || null },
    { key: "dm-projects", label: "DM Projects", count: dmProjectCount || null },
  ];
  const requestedTab = searchParams.get("tab");
  const activeTab = tabs.some((t) => t.key === requestedTab) ? requestedTab : "profile";
  const setTab = (key) => setSearchParams(key === "profile" ? {} : { tab: key }, { replace: true });

  const stats = [
    { key: "profile", label: "DM services", value: services.length, hint: services.map((s) => SERVICE_INFO[s]?.label || s).join(" · ") || null, Icon: FaGlobe, tone: "amber" },
    { key: "accounts", label: "Social accounts", value: accounts.length, hint: accounts.length ? `${activeAccounts} active` : null, Icon: FaShareAlt, tone: "green" },
    { key: "projects", label: isServiceBased ? "Client services" : "Client projects", value: offerings.length, Icon: FaFolderOpen, tone: "blue" },
    { key: "dm-projects", label: "DM projects", value: dmProjectCount ?? "…", Icon: FaBullhorn, tone: "violet" },
  ];

  return (
    <div className="dmp dmc">
      <div className="dmc-topbar">
        <Link to={basePath} className="dmc-back">
          <FaArrowLeft /> Change client
        </Link>
      </div>

      <div className="dmc-hero">
        <div className="dmc-hero__avatar" aria-hidden="true">
          {initials(name)}
        </div>
        <div className="dmc-hero__body">
          <div className="dmc-hero__title-row">
            <h2 className="dmc-hero__title">{name}</h2>
            <span className={`dmc-pill dmc-pill--${enrolled ? "success" : "warning"}`}>
              {enrolled ? "DM enrolled" : access?.allowed_by_business_type ? "Not enrolled" : "DM not available"}
            </span>
          </div>
          <div className="dmc-hero__sub">
            {overview.Client_Name ? (
              <span>
                <FaUserCircle /> {overview.Client_Name}
              </span>
            ) : null}
            {overview.Business_Type || modelLabel ? (
              <span className="dmc-hero__types">
                {overview.Business_Type ? <span className="dmc-hero__type">{overview.Business_Type}</span> : null}
                {modelLabel ? <span className="dmc-hero__type">{modelLabel}</span> : null}
              </span>
            ) : null}
          </div>
          <div className="dmc-hero__contacts">
            {overview.Mobile ? (
              <a
                className="dmc-chip"
                href={`tel:${String(overview.Mobile).replace(/[^\d+]/g, "")}`}
                title="Call"
              >
                <FaPhoneAlt aria-hidden="true" />
                <span className="dmc-chip__text">{overview.Mobile}</span>
                <FaChevronRight className="dmc-chip__go" aria-hidden="true" />
              </a>
            ) : null}
            {overview.Email ? (
              <a className="dmc-chip" href={`mailto:${overview.Email}`} title="Send email">
                <FaEnvelope aria-hidden="true" />
                <span className="dmc-chip__text">{overview.Email}</span>
                <FaChevronRight className="dmc-chip__go" aria-hidden="true" />
              </a>
            ) : null}
            {overview.Website ? (
              <a
                className="dmc-chip"
                href={websiteHref(overview.Website)}
                target="_blank"
                rel="noreferrer"
                title="Open website"
              >
                <FaGlobe aria-hidden="true" />
                <span className="dmc-chip__text">
                  {overview.Website.replace(/^https?:\/\//i, "").replace(/\/$/, "")}
                </span>
                <FaExternalLinkAlt className="dmc-chip__go" aria-hidden="true" />
              </a>
            ) : null}
            {overview.Portal_URL ? (
              <a
                className="dmc-chip"
                href={overview.Portal_URL}
                target="_blank"
                rel="noreferrer"
                title={`Login portal (${portalTypeLabel(overview.Portal_Type)})`}
              >
                <FaFolderOpen aria-hidden="true" />
                <span className="dmc-chip__text">{overview.Portal_Host}</span>
                <FaExternalLinkAlt className="dmc-chip__go" aria-hidden="true" />
              </a>
            ) : null}
          </div>
        </div>
        <div className="dmc-hero__actions">
          <button type="button" className="dmc-btn dmc-btn--outline" onClick={editTenant}>
            <FaPen /> Edit client / services
          </button>
        </div>
      </div>

      {!enrolled ? (
        <div className="dmp-notice">
          {access?.allowed_by_business_type
            ? "This client is not enrolled in a Digital Marketing service. "
            : "Digital Marketing is not available for this client's business type. "}
          {access?.allowed_by_business_type ? (
            <Link to={`/tenants/${overview.tenant_id}/edit`}>Assign a DM service from client edit</Link>
          ) : null}
        </div>
      ) : null}

      <div className="dmc-stats">
        {stats.map(({ key, label, value, hint, Icon, tone }) => (
          <button key={label} type="button" className={`dmc-stat dmc-stat--${tone}`} onClick={() => setTab(key)}>
            <span className="dmc-stat__icon">
              <Icon />
            </span>
            <span className="dmc-stat__body">
              <span className="dmc-stat__value">{value}</span>
              <span className="dmc-stat__label">{label}</span>
              {hint ? <span className="dmc-stat__hint">{hint}</span> : null}
            </span>
          </button>
        ))}
      </div>

      <div className="dmp-tabs dmc-tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            className={`dmp-tab${activeTab === t.key ? " is-active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.count ? <span className="dmp-tab__count">{t.count}</span> : null}
          </button>
        ))}
      </div>
      <div className="dmc-tab-select">
        <select aria-label="Section" value={activeTab} onChange={(e) => setTab(e.target.value)}>
          {tabs.map((t) => (
            <option key={t.key} value={t.key}>
              {t.count ? `${t.label} (${t.count})` : t.label}
            </option>
          ))}
        </select>
        <FaChevronDown className="dmc-tab-select__icon" aria-hidden="true" />
      </div>

      {activeTab === "profile" ? (
        <div className="dmc-grid">
          <section className="dmc-card">
            <h3 className="dmc-card__title">Business information</h3>
            <div className="dmc-info-grid">
              <Info label="Business name" value={overview.Business_Name} />
              <Info label="Client name" value={overview.Client_Name} />
              <Info label="Business type" value={overview.Business_Type} />
              <Info label="Business model" value={modelLabel || "Not set"} />
              <Info
                label="Website"
                value={overview.Website}
                href={overview.Website ? websiteHref(overview.Website) : undefined}
                external
              />
            </div>
          </section>

          <section className="dmc-card">
            <h3 className="dmc-card__title">Contact</h3>
            <div className="dmc-info-grid">
              <Info
                label="Mobile"
                value={overview.Mobile}
                href={overview.Mobile ? `tel:${String(overview.Mobile).replace(/[^\d+]/g, "")}` : undefined}
              />
              <Info label="Email" value={overview.Email} href={overview.Email ? `mailto:${overview.Email}` : undefined} />
            </div>
          </section>

          <section className="dmc-card dmc-card--wide">
            <div className="dmc-card__head">
              <h3 className="dmc-card__title">Digital Marketing services</h3>
              <button type="button" className="dmc-link-btn" onClick={editTenant}>
                Manage services
              </button>
            </div>
            {services.length === 0 ? (
              <p className="dmc-empty">No Digital Marketing service assigned yet.</p>
            ) : (
              <div className="dmc-services">
                {services.map((s) => {
                  const info = SERVICE_INFO[s] || { label: s, title: s, tone: "content" };
                  return (
                    <div key={s} className={`dmc-service dmc-service--${info.tone}`}>
                      <span className="dmc-service__code">{info.label}</span>
                      <span className="dmc-service__title">{info.title}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      ) : null}

      {activeTab === "projects" && addingProject && isServiceBased ? (
        <ClientServiceForm
          tenantId={overview.tenant_id}
          tenantName={name}
          onCancel={() => setAddingProject(false)}
          onSaved={() => {
            setAddingProject(false);
            reloadOverview();
          }}
        />
      ) : null}

      {activeTab === "projects" && isServiceBased ? (
        <section className="dmc-card">
          <div className="dmc-card__head">
            <h3 className="dmc-card__title">Services</h3>
            <span className="dmp-muted">Services this client markets (service based business)</span>
            {!addingProject ? (
              <button type="button" className="dmp-btn dmp-btn--sm" onClick={() => setAddingProject(true)}>
                + Add service
              </button>
            ) : null}
          </div>
          {tenantServices.length > 0 ? (
            <div className="dmc-project-search">
              <input
                type="search"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="Search by service name or description"
              />
              {projectSearch ? (
                <span className="dmp-muted">
                  {filteredServices.length} of {tenantServices.length}
                </span>
              ) : null}
            </div>
          ) : null}
          {tenantServices.length === 0 ? (
            <p className="dmc-empty">
              No services recorded for this client yet.
              {!addingProject ? " Use “Add service” to create one." : ""}
            </p>
          ) : filteredServices.length === 0 ? (
            <p className="dmc-empty">No services match “{projectSearch}”.</p>
          ) : (
            <>
            <ResultsMobileList>
              {filteredServices.map((s) => (
                <ResultsMobileCard
                  key={s.Tenant_Client_Service_Id}
                  title={s.Service_Name}
                  meta={s.Status ? [{ label: s.Status, tone: statusTone(s.Status), status: true }] : []}
                  fields={[{ label: "Description", value: s.Description }]}
                />
              ))}
            </ResultsMobileList>
            <div className="dmp-table-wrap co-desktop-table">
              <table className="dmc-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Description</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredServices.map((s) => (
                    <tr key={s.Tenant_Client_Service_Id}>
                      <td className="dmc-table__strong">{s.Service_Name}</td>
                      <td className="dmc-table__clamp" title={s.Description || ""}>
                        {s.Description || <span className="dmp-muted">—</span>}
                      </td>
                      <td>
                        {s.Status ? <span className={`dmc-pill dmc-pill--${statusTone(s.Status)}`}>{s.Status}</span> : <span className="dmp-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
      ) : null}

      {activeTab === "projects" && addingProject && !isServiceBased ? (
        <ClientProjectForm
          tenantId={overview.tenant_id}
          tenantName={name}
          onCancel={() => setAddingProject(false)}
          onSaved={() => {
            setAddingProject(false);
            reloadOverview();
          }}
        />
      ) : null}

      {activeTab === "projects" && !isServiceBased ? (
        <section className="dmc-card">
          <div className="dmc-card__head">
            <h3 className="dmc-card__title">Projects</h3>
            <span className="dmp-muted">Client projects can be linked to DM projects</span>
            {!addingProject ? (
              <button type="button" className="dmp-btn dmp-btn--sm" onClick={() => setAddingProject(true)}>
                + Add project
              </button>
            ) : null}
          </div>
          {tenantProjects.length > 0 ? (
            <div className="dmc-project-search">
              <input
                type="search"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="Search by project name, code, type or location"
              />
              {projectSearch ? (
                <span className="dmp-muted">
                  {filteredProjects.length} of {tenantProjects.length}
                </span>
              ) : null}
            </div>
          ) : null}
          {tenantProjects.length === 0 ? (
            <p className="dmc-empty">
              No projects recorded for this client yet.
              {!addingProject ? " Use “Add project” to create one." : ""}
            </p>
          ) : filteredProjects.length === 0 ? (
            <p className="dmc-empty">No projects match “{projectSearch}”.</p>
          ) : (
            <>
            <ResultsMobileList>
              {filteredProjects.map((p) => {
                const hasLinks = p.Project_URL || p.Brochure_URL || p.Has_Property_Details;
                return (
                  <ResultsMobileCard
                    key={p.Tenant_Project_Id}
                    title={p.Project_Name}
                    meta={[
                      p.Status ? { label: p.Status, tone: statusTone(p.Status), status: true } : null,
                      p.Project_Type ? { label: p.Project_Type, tone: "info" } : null,
                    ].filter(Boolean)}
                    fields={[
                      { label: "Code", value: p.Short_Code ? String(p.Short_Code).toUpperCase() : p.Short_Code },
                      { label: "Address", value: p.Address },
                      { label: "Description", value: p.Description },
                    ]}
                    actions={
                      hasLinks ? (
                        <div className="co-mcard__action-grid">
                          {p.Project_URL ? (
                            <a className="co-maction" href={p.Project_URL} target="_blank" rel="noreferrer" title="Project page">
                              <FaGlobe size={14} />
                              <span>Page</span>
                            </a>
                          ) : null}
                          {p.Brochure_URL ? (
                            <a className="co-maction" href={p.Brochure_URL} target="_blank" rel="noreferrer" title="View brochure">
                              <FaExternalLinkAlt size={13} />
                              <span>Brochure</span>
                            </a>
                          ) : null}
                          {p.Has_Property_Details ? (
                            <button type="button" className="co-maction" title="Download property details" onClick={() => downloadDetails(p)}>
                              <FaDownload size={14} />
                              <span>JSON</span>
                            </button>
                          ) : null}
                        </div>
                      ) : null
                    }
                  />
                );
              })}
            </ResultsMobileList>
            <div className="dmp-table-wrap co-desktop-table">
              <table className="dmc-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Address</th>
                    <th>Description</th>
                    <th>Links</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((p) => (
                    <tr key={p.Tenant_Project_Id}>
                      <td className="dmc-table__strong">{p.Project_Name}</td>
                      <td>{p.Short_Code ? String(p.Short_Code).toUpperCase() : <span className="dmp-muted">—</span>}</td>
                      <td>{p.Project_Type || <span className="dmp-muted">—</span>}</td>
                      <td>{p.Address || <span className="dmp-muted">—</span>}</td>
                      <td className="dmc-table__clamp" title={p.Description || ""}>
                        {p.Description || <span className="dmp-muted">—</span>}
                      </td>
                      <td className="dmc-table__nowrap">
                        {p.Project_URL ? (
                          <a href={p.Project_URL} target="_blank" rel="noreferrer">
                            Page
                          </a>
                        ) : null}
                        {p.Project_URL && (p.Brochure_URL || p.Has_Property_Details) ? " · " : null}
                        {p.Brochure_URL ? (
                          <a href={p.Brochure_URL} target="_blank" rel="noreferrer">
                            View
                          </a>
                        ) : null}
                        {p.Brochure_URL && p.Has_Property_Details ? " · " : null}
                        {p.Has_Property_Details ? (
                          <button type="button" className="dmp-link" onClick={() => downloadDetails(p)}>
                            JSON
                          </button>
                        ) : null}
                        {!p.Project_URL && !p.Brochure_URL && !p.Has_Property_Details ? <span className="dmp-muted">—</span> : null}
                      </td>
                      <td>
                        {p.Status ? <span className={`dmc-pill dmc-pill--${statusTone(p.Status)}`}>{p.Status}</span> : <span className="dmp-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
      ) : null}

      {activeTab === "dm-projects" ? (
        <ProjectList
          api={api}
          meta={meta}
          basePath={basePath}
          tenantId={overview.tenant_id}
          tenantName={name}
          canCreate={!!enrolled}
        />
      ) : null}

      {activeTab === "accounts" ? (
        <section className="dmc-card">
          <div className="dmc-card__head">
            <h3 className="dmc-card__title">Social accounts</h3>
            <button type="button" className="dmc-btn dmc-btn--outline dmc-btn--sm" onClick={manageAccounts}>
              Manage accounts
            </button>
          </div>
          {accounts.length === 0 ? (
            <p className="dmc-empty">No accounts added yet (Gmail, GMB, Facebook, Instagram, LinkedIn, YouTube, WA Business).</p>
          ) : (
            <div className="dmc-accounts">
              {accounts.map((a) => {
                const { Icon, color } = platformIcon(a.Platform_Name);
                const url = a.Profile_URL || a.Business_URL;
                return (
                  <div key={a.Account_Id} className="dmc-account">
                    <span className="dmc-account__icon" style={{ color, background: `${color}14` }}>
                      <Icon />
                    </span>
                    <div className="dmc-account__body">
                      <div className="dmc-account__top">
                        <strong className="dmc-account__platform">{a.Platform_Name}</strong>
                        {a.Status ? <span className={`dmc-pill dmc-pill--${statusTone(a.Status)}`}>{a.Status}</span> : null}
                      </div>
                      <div className="dmc-account__name">{a.Account_Name || "—"}</div>
                      <div className="dmc-account__user">{a.Email_Used || a.Username || "—"}</div>
                    </div>
                    {url ? (
                      <a className="dmc-account__open" href={url} target="_blank" rel="noreferrer" title="Open profile">
                        <FaExternalLinkAlt />
                      </a>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

export default DMClientDetails;
